if (!customElements.get("carousel-slider")) {
  customElements.define(
    "carousel-slider",
    class CarouselSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.flickity) return;
        this.carousel = this.querySelector('[class*="grid-carousel"]');
        if (this.carousel) this.initialize();
      }

      disconnectedCallback() {
        if (this.flickity) this.flickity.destroy();
        this.flickity = null;
        if (this.carousel) this.carousel.style.display = this.previousDisplay;
      }

      initialize() {
        const autoplayEnabled = this.getAttribute("data-autoplay") === "true";
        const autoPlaySpeed = parseInt(this.getAttribute("data-speed"), 10) * 1000;
        const groupCells = this.getAttribute("data-group-cells") !== "false";
        const wrapAround = this.getAttribute("data-wrap-around") === "true";

        // Flickity doesn't work with flex containers, so
        // to avoid flash of unstyled content and page jump, style the container
        // as block just before initialization. On mobile where the carousel
        // is converted to simple scroller we swap this to display: flex !important
        // using CSS.
        this.previousDisplay = this.carousel.style.display;
        this.carousel.style.display = "block";
        this.flickity = new Flickity(this.carousel, {
          autoPlay: autoplayEnabled ? autoPlaySpeed : false,
          cellAlign: "left",
          groupCells: groupCells,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          fade: false,
          cellSelector: ".grid-item",
          initialIndex: 0,
          pageDots: false,
          wrapAround: wrapAround,
          accessibility: false,
          watchCSS: true,
        });

        this.flickity.on("change", (index) => {
          const event = new CustomEvent("slider:slide-change", {
            detail: {
              id: this.getAttribute("id"),
              index,
            },
          });
          document.dispatchEvent(event);
        });
        document.dispatchEvent(new CustomEvent("carousel:ready", {
          detail: { component: this },
        }));
      }
    },
  );
}

if (!customElements.get("carousel-navigation")) {
  customElements.define(
    "carousel-navigation",
    class CarouselNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.navigationController = new AbortController();
        document.addEventListener("carousel:ready", this.init, {
          signal: this.navigationController.signal,
        });
        this.init();
      }

      disconnectedCallback() {
        this.navigationController?.abort();
        this.buttonController?.abort();
        if (this.flickity && this.handleSelect) {
          this.flickity.off("select", this.handleSelect);
        }
        this.flickity = null;
      }

      init = () => {
        if (!this.isConnected) return;
        const id = this.getAttribute("for");
        this.carouselComponent = document.getElementById(id);
        if (!this.carouselComponent?.flickity || this.flickity === this.carouselComponent.flickity) return;
        this.buttonController?.abort();
        if (this.flickity && this.handleSelect) this.flickity.off("select", this.handleSelect);
        this.buttonController = new AbortController();
        this.wrap = this.dataset.wrap === "true";
        this.flickity = this.carouselComponent.flickity;
        this.buttonPrev = this.querySelector(".js-prev");
        this.buttonNext = this.querySelector(".js-next");
        const options = { signal: this.buttonController.signal };
        this.buttonPrev?.addEventListener("click", (event) => {
          event.preventDefault();
          this.flickity.previous();
        }, options);
        this.buttonNext?.addEventListener("click", (event) => {
          event.preventDefault();
          this.flickity.next();
        }, options);
        this.handleSelect = () => {
          if (this.wrap) return;
          this.enableButton(this.buttonPrev);
          this.enableButton(this.buttonNext);
          // Flickity has no slides yet while it is inactive (e.g. watchCSS breakpoints).
          const slides = this.flickity.slides || [];
          if (!slides[this.flickity.selectedIndex - 1]) {
            this.disableButton(this.buttonPrev);
          }
          if (!slides[this.flickity.selectedIndex + 1]) {
            this.disableButton(this.buttonNext);
          }
        };
        this.flickity.on("select", this.handleSelect);
        this.handleSelect();
      };

      enableButton(button) {
        if (!button) return;
        button.classList.remove("pill-nav-item-disabled");
        button.classList.remove("focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        if (!button) return;
        button.classList.add("pill-nav-item-disabled");
        button.classList.add("focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}
